import { fetchStoredBytes } from './fileUrls';

/**
 * One-click download for a document that lives in Supabase Storage.
 *
 * Why this exists: an `<a href={signedUrl} download="...">` does NOT download.
 * The signed URL points at the Supabase host, and browsers ignore the `download`
 * attribute on a cross-origin link — the file just opens in a tab instead. The
 * only reliable way is to pull the bytes ourselves and hand the browser a blob
 * URL from our own origin, which is what DocumentsView already does.
 */

// Uploads are stored with a timestamp (and sometimes a random) prefix so two
// files with the same name can't collide in the bucket — see processFileUpload
// in LoadDetailView. That prefix should never reach the user's Downloads folder.
const STORAGE_KEY_PREFIX = /^\d{10,}[-_](?:[a-z0-9]{4,10}[-_])?/i;

const EXTENSION = /\.[a-z0-9]{2,5}$/i;

/**
 * Pick the name the file gets on disk: the clean name carried by the row
 * (`documents.name` is the original file name), falling back to the storage
 * object name with its collision prefix stripped.
 */
export function resolveDownloadName(preferredName: string | null | undefined, pathOrUrl: string): string {
  let objectName = '';
  try {
    objectName = decodeURIComponent((pathOrUrl.split('?')[0].split('/').pop() || '').trim());
  } catch {
    objectName = (pathOrUrl.split('?')[0].split('/').pop() || '').trim();
  }
  const cleanObjectName = objectName.replace(STORAGE_KEY_PREFIX, '');

  const candidate = (preferredName || '').trim() || cleanObjectName || 'document';
  if (EXTENSION.test(candidate)) return candidate;

  // A few rows carry a label with no extension ('Document', 'Untitled Document');
  // borrow the extension from the stored object so the file opens on the machine.
  return `${candidate}${cleanObjectName.match(EXTENSION)?.[0] || ''}`;
}

/**
 * Downloads a document straight to disk — no preview, no new tab.
 * Throws on failure so the caller can toast; never swallows the error.
 *
 * @param pathOrUrl storage path inside the bucket, a stored storage URL (public
 *                  or signed; read through a fresh signature, so it works on a
 *                  private bucket and never returns a stale cached copy), or an
 *                  external http(s) URL (SMS/webhook attachments are hosted
 *                  outside Supabase)
 * @param preferredName clean file name from the row, when there is one
 * @param bucket bucket for a bare path, `company-documents` for every load document
 */
export async function downloadDocument(
  pathOrUrl: string,
  preferredName?: string | null,
  bucket: string = 'company-documents'
): Promise<void> {
  if (!pathOrUrl) throw new Error('This document has no file attached');

  const blob = await fetchStoredBytes(pathOrUrl, { bucket });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = resolveDownloadName(preferredName, pathOrUrl);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
